// client/src/utils/listTools.js — the pure half of the shared list (components/shared/DataList):
// search over every column, filters, sort, and CSV of exactly the rows shown.
// Kept free of React so scripts/test-list-tools.js can exercise it under Node.

/** The value a column holds for a row: column.value(row) if given, else row[column.key]. */
export function cellValue(row, col) {
    const v = typeof col.value === 'function' ? col.value(row) : row[col.key];
    return v === undefined ? null : v;
}

const asText = (v) => (v === null || v === undefined ? '' : v instanceof Date ? v.toISOString() : String(v));

/** Rows where every word of `query` appears in some column (case-insensitive). */
export function searchRows(rows, columns, query) {
    const words = String(query || '').toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return rows;
    const cols = columns.filter((c) => c.search !== false);
    return rows.filter((r) => {
        const hay = cols.map((c) => asText(cellValue(r, c))).join(' \u0001 ').toLowerCase();
        return words.every((w) => hay.includes(w));
    });
}

/** filters = { key: value } with value '' meaning "any". */
export function filterRows(rows, columns, filters) {
    const active = Object.entries(filters || {}).filter(([, v]) => v !== '' && v !== null && v !== undefined);
    if (!active.length) return rows;
    return rows.filter((r) => active.every(([k, v]) => {
        const col = columns.find((c) => c.key === k) || { key: k };
        return asText(cellValue(r, col)) === String(v);
    }));
}

/** Distinct values of a column, for a filter select. */
export function distinctValues(rows, col) {
    return [...new Set(rows.map((r) => asText(cellValue(r, col))).filter((v) => v !== ''))].sort((a, b) => a.localeCompare(b));
}

const NUM_RE = /^-?\$?[\d,]+(\.\d+)?%?$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}/;
function comparable(v, type) {
    if (v === null || v === undefined || v === '') return null;
    if (type === 'number' || (typeof v === 'number')) return Number(String(v).replace(/[$,%]/g, ''));
    if (type === 'date' || v instanceof Date || DATE_RE.test(String(v))) { const t = new Date(v).getTime(); return Number.isNaN(t) ? String(v) : t; }
    const s = String(v);
    if (NUM_RE.test(s.trim())) return Number(s.replace(/[$,%]/g, ''));
    return s.toLowerCase();
}

/** Stable sort; blanks always last. */
export function sortRows(rows, columns, sort) {
    if (!sort || !sort.key) return rows;
    const col = columns.find((c) => c.key === sort.key) || { key: sort.key };
    const dir = sort.dir === 'asc' ? 1 : -1;
    const sv = typeof col.sortValue === 'function' ? col.sortValue : (r) => cellValue(r, col);
    return rows.map((r, i) => ({ r, i, v: comparable(sv(r), col.type) })).sort((a, b) => {
        if (a.v === null && b.v === null) return a.i - b.i;
        if (a.v === null) return 1;
        if (b.v === null) return -1;
        if (a.v < b.v) return -dir;
        if (a.v > b.v) return dir;
        return a.i - b.i;
    }).map((x) => x.r);
}

/** The default "newest first" sort: the first date-ish column, else the first column, descending. */
export function defaultSortFor(columns) {
    const c = columns.find((x) => x.type === 'date') || columns.find((x) => /(_at|date)$/.test(x.key)) || columns[0];
    return c ? { key: c.key, dir: 'desc' } : null;
}

const csvCell = (v) => {
    const s = asText(v);
    // Neutralise spreadsheet formulas (CSV injection) and quote when needed.
    const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
    return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

/** CSV text of the rows shown, in the columns shown (columns with csv:false are skipped). */
export function toCsv(rows, columns) {
    const cols = columns.filter((c) => c.csv !== false);
    const head = cols.map((c) => csvCell(c.label || c.key)).join(',');
    const body = rows.map((r) => cols.map((c) => csvCell(cellValue(r, c))).join(','));
    return [head, ...body].join('\r\n');
}

/** The whole pipeline. */
export function applyList(rows, columns, { query, filters, sort }) {
    return sortRows(filterRows(searchRows(rows || [], columns, query), columns, filters), columns, sort);
}

/** Stable partition: rows where pin(row) is true first, each group keeping its order. */
export function pinRows(rows, pin) {
    if (typeof pin !== 'function') return rows;
    return [...rows.filter((r) => pin(r)), ...rows.filter((r) => !pin(r))];
}
